import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { editWorkingInterview } from '~/actions/PermanentJobs';
import { BackButton, Box, Column, CustomSpinner, FloatingContainer, Row, Screen, Text } from '~/commonComponents';
import { useAppSelector, useGetOfficeById } from '~/hooks';
import {
  TWorkingInterviewFormData,
  WorkingInterviewForm,
  WorkingInterviewFormFields,
} from '../components';
import { useGetDraftJobPost } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { ProfessionTypeEnum } from '~/models';

type TRouteParams = {
  postingId: string;
  workingInterviewId: string;
};

export const EditWorkingInterviewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { postingId, workingInterviewId } = useParams<TRouteParams>();

  const interviews = useAppSelector((state) => state.permanentJobs.workingInterviews);

  const { data: draftData, isLoading, isError, error } = useGetDraftJobPost(postingId);

  const { officeId, title, subProfession, professionType } = draftData || {};

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error, dispatch]);

  // Get office details using the reusable hook
  const office = useGetOfficeById(officeId);
  const officeName = office?.office_name || '';

  const defaultValues = useMemo(() => {
    if (!postingId || !workingInterviewId) return undefined;

    const currentInterview = interviews[postingId]?.find((it) => it.id === workingInterviewId);

    if (!currentInterview) return undefined;

    return {
      breakTime: currentInterview.breakTime,
      dates: [new Date(currentInterview.date)],
      hourlyRate: currentInterview.hourlyRate,
      hoursArrival: currentInterview.hoursArrival,
      hoursDeparture: currentInterview.hoursDeparture,
      isAssistedHygiene: currentInterview.isAssistedHygiene,
      isPaidBreakTime: currentInterview.isPaidBreakTime,
      specialty: currentInterview.specialty,
    } as TWorkingInterviewFormData;
  }, [interviews, postingId, workingInterviewId]);

  const handleOnSubmit = (formData: TWorkingInterviewFormData) => {
    if (!postingId || !workingInterviewId) return;

    dispatch(
      editWorkingInterview(
        {
          breakTime: formData.breakTime,
          hourlyRate: formData.hourlyRate,
          hoursArrival: formData.hoursArrival,
          hoursDeparture: formData.hoursDeparture,
          id: workingInterviewId,
          isAssistedHygiene: formData.isAssistedHygiene,
          isPaidBreakTime: formData.isPaidBreakTime,
          specialty: formData.specialty,
        },
        postingId,
      ),
    );

    navigate(`/permanent-jobs/postings/${postingId}/review-and-payment`);
  };

  if (isLoading) {
      return <CustomSpinner isLoading />;
    }

  return (
    <Screen>
      <FloatingContainer borderRadius={4} paddingBlock={48} paddingInline={16}>
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0}>
            <BackButton />
          </Box>
          <Text bold fontSize={32} textAlign="center">
            Edit Working Interview
          </Text>
        </Row>
        <Column marginInline="auto" marginTop={40} width={680}>
          <WorkingInterviewForm
            defaultValues={defaultValues}
            isEdit
            jobTitle={title || ''}
            office={officeName}
            profession={subProfession || professionType || ProfessionTypeEnum.RDH}
            readonlyFields={[WorkingInterviewFormFields.Dates]}
            unavailableDates={defaultValues?.dates}
            onSubmit={handleOnSubmit}
          />
        </Column>
      </FloatingContainer>
    </Screen>
  );
};
